import { ReactNode } from "react";
import { motion } from "framer-motion";
import { ThumbsUp, ThumbsDown, Lightbulb } from "lucide-react";
import type { LunimarRealm } from "@/components/lunimar/lunimarRealms";

export interface DecisionRecap {
  label: string;
  detail?: string;
}

interface Props {
  realm?: LunimarRealm;
  strongest?: DecisionRecap;
  weakest?: DecisionRecap;
  tradeoffLearned?: string;
  realWorldTakeaway?: string;
  children?: ReactNode;
}

/**
 * "Mission debrief" recap card — best/weakest decisions, tradeoff learned,
 * and the real-world takeaway. Use inside MissionResultPanel.
 */
const ModuleRecapCard = ({
  realm,
  strongest,
  weakest,
  tradeoffLearned,
  realWorldTakeaway,
  children,
}: Props) => {
  const accent = realm?.primaryAccent ?? "210 90% 64%";
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {strongest && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-xl border p-3"
          style={{
            borderColor: "hsl(152 70% 55% / 0.4)",
            background: "hsl(152 70% 32% / 0.12)",
          }}
        >
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
               style={{ color: "hsl(152 70% 65%)" }}>
            <ThumbsUp className="h-3 w-3" /> Strongest move
          </div>
          <div className="mt-1 font-heading text-sm font-semibold text-white">{strongest.label}</div>
          {strongest.detail && (
            <p className="mt-1 text-[11px] leading-relaxed text-white/70">{strongest.detail}</p>
          )}
        </motion.div>
      )}
      {weakest && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="rounded-xl border p-3"
          style={{
            borderColor: "hsl(38 95% 55% / 0.4)",
            background: "hsl(38 95% 32% / 0.12)",
          }}
        >
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
               style={{ color: "hsl(38 95% 65%)" }}>
            <ThumbsDown className="h-3 w-3" /> Weakest move
          </div>
          <div className="mt-1 font-heading text-sm font-semibold text-white">{weakest.label}</div>
          {weakest.detail && (
            <p className="mt-1 text-[11px] leading-relaxed text-white/70">{weakest.detail}</p>
          )}
        </motion.div>
      )}
      {tradeoffLearned && (
        <div
          className="rounded-xl border p-3 sm:col-span-2"
          style={{
            borderColor: `hsl(${accent} / 0.35)`,
            background: `hsl(${accent} / 0.08)`,
          }}
        >
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
               style={{ color: `hsl(${accent})` }}>
            <Lightbulb className="h-3 w-3" /> Tradeoff you mastered
          </div>
          <p className="mt-1 text-xs leading-relaxed text-white/85">{tradeoffLearned}</p>
        </div>
      )}
      {realWorldTakeaway && (
        <div
          className="rounded-xl border border-white/10 bg-white/5 p-3 sm:col-span-2"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/55">
            Real-world takeaway
          </div>
          <p className="mt-1 text-xs leading-relaxed text-white/80">{realWorldTakeaway}</p>
        </div>
      )}
      {children}
    </div>
  );
};

export default ModuleRecapCard;